import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CleaningSchedule, CleaningScheduleEntry,
  getCleaningSchedule, startCleaningTask, finishCleaningTask, updateCleaningTaskNotes,
} from '../api.cleaners';
import { cleaningCalendarText, CleaningCalendarText as T } from '../i18n/pages/cleaningCalendar';
import type { Lang } from '../i18n/types';

const CLEANER_LANG_KEY = 'cleaner-lang';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getLocalToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const TODAY = getLocalToday();

const formatDate = (iso: string, lang: Lang) => {
  const d = new Date(iso + 'T00:00:00');
  const locale = lang === 'cs' ? 'cs-CZ' : lang === 'uk' ? 'uk-UA' : lang === 'vi' ? 'vi-VN' : 'en-GB';
  return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
};

const isToday = (iso: string) => iso === TODAY;

const truncate = (s: string | null, max = 45) =>
  !s ? null : s.length <= max ? s : s.slice(0, max).trimEnd() + '…';

const groupByDate = (entries: CleaningScheduleEntry[]) => {
  const map = new Map<string, CleaningScheduleEntry[]>();
  for (const e of entries) {
    const list = map.get(e.date) ?? [];
    list.push(e);
    map.set(e.date, list);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
};

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: CleaningScheduleEntry['status']; t: T }> = ({ status, t }) => {
  const styles: Record<string, React.CSSProperties> = {
    PENDING:     { background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
    IN_PROGRESS: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
    DONE:        { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
  };
  const labels: Record<string, string> = {
    PENDING: t.statusPending, IN_PROGRESS: t.statusInProgress, DONE: t.statusDone,
  };
  return (
    <span style={{ ...styles[status], padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' }}>
      {labels[status]}
    </span>
  );
};

// ── Guest count ───────────────────────────────────────────────────────────────

const GuestCount: React.FC<{ label: string; value: number | null; color: string; guestsLabel: string }> = ({ label, value, color, guestsLabel }) => (
  <div style={{ textAlign: 'center', minWidth: 52 }}>
    <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
    <div style={{ fontWeight: 700, fontSize: '1.35rem', color, lineHeight: 1 }}>{value != null ? value : '?'}</div>
    <div style={{ fontSize: '0.68rem', color: '#718096', marginTop: 1 }}>{guestsLabel}</div>
  </div>
);

// ── Task card ─────────────────────────────────────────────────────────────────

interface TaskCardProps {
  entry: CleaningScheduleEntry;
  privateKey: string;
  defaultExpanded: boolean;
  t: T;
  onUpdate: (updated: CleaningScheduleEntry) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ entry, privateKey, defaultExpanded, t, onUpdate }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [busy, setBusy] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(entry.notes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setNotesDraft(entry.notes ?? ''); }, [entry.notes]);

  const doAction = async (action: 'start' | 'finish') => {
    setBusy(true);
    try {
      const updated = action === 'start'
        ? await startCleaningTask(privateKey, entry.reservationId)
        : await finishCleaningTask(privateKey, entry.reservationId);
      onUpdate(updated);
      if (action === 'finish') setExpanded(false);
    } catch (e: any) {
      alert(e.response?.data?.message || e.message || t.actionFailed);
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await updateCleaningTaskNotes(privateKey, entry.reservationId, notesDraft);
      onUpdate(updated);
      setNotesOpen(false);
    } catch (e: any) {
      alert(e.response?.data?.message || e.message || t.notesSaveFailed);
    } finally {
      setSavingNotes(false);
    }
  };

  const borderColor = entry.status === 'DONE' ? '#86efac' : entry.status === 'IN_PROGRESS' ? '#93c5fd' : '#e2e8f0';
  const notePreview = truncate(entry.notes);

  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: 10, background: '#fff', marginBottom: 10, overflow: 'hidden' }}>

      {/* ── Always-visible header ─── */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.apartmentName}
        </div>
        <StatusBadge status={entry.status} t={t} />
        <span style={{ fontSize: '0.8rem', color: '#a0aec0', flexShrink: 0, marginLeft: 2 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Note preview in collapsed mode */}
      {!expanded && notePreview && (
        <div style={{ padding: '0 14px 10px', fontSize: '0.82rem', color: '#718096', fontStyle: 'italic' }}>
          {notePreview}
        </div>
      )}

      {/* ── Expanded body ─── */}
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${borderColor}` }}>

          {/* Guest counts */}
          <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <GuestCount label={t.leaving} value={entry.leavingGuests} color="#e53e3e" guestsLabel={t.guests} />
            <div style={{ width: 1, height: 40, background: '#e2e8f0', flexShrink: 0 }} />
            <GuestCount label={t.arriving} value={entry.arrivingGuests} color="#2b6cb0" guestsLabel={t.guests} />
            {entry.estimatedArrivalTime && (
              <>
                <div style={{ width: 1, height: 40, background: '#e2e8f0', flexShrink: 0 }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{t.arrival}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#2d3748', lineHeight: 1 }}>{entry.estimatedArrivalTime}</div>
                </div>
              </>
            )}
          </div>

          {/* The actual window for this turnover. Highlighted only when a guest has bought hours
              that move it — an ordinary turnover needs no extra emphasis, and flagging every row
              would train the eye to skip the ones that matter. */}
          {(entry.departureExtended || entry.arrivalBroughtForward) && (
            <div style={{
              marginTop: 12, padding: '9px 12px', borderRadius: 8,
              background: '#fffbeb', border: '1px solid #fcd34d',
              fontSize: '0.85rem', color: '#92400e', display: 'flex', gap: 14, flexWrap: 'wrap',
            }}>
              {entry.departureExtended && entry.departureTime && (
                <span><strong>{entry.departureTime}</strong> ⟵ {t.leaving}</span>
              )}
              {entry.arrivalBroughtForward && entry.arrivalTime && (
                <span>{t.arriving} ⟶ <strong>{entry.arrivalTime}</strong></span>
              )}
            </div>
          )}

          {/* Notes display */}
          {entry.notes && !notesOpen && (
            <div style={{ marginTop: 12, padding: '7px 10px', background: '#f7fafc', borderRadius: 7, fontSize: '0.85rem', color: '#4a5568', borderLeft: '3px solid #cbd5e0' }}>
              {entry.notes}
            </div>
          )}

          {/* Notes editor */}
          {notesOpen && (
            <div style={{ marginTop: 12 }}>
              <textarea
                ref={notesRef}
                value={notesDraft}
                onChange={e => setNotesDraft(e.target.value.slice(0, 100))}
                rows={2}
                maxLength={100}
                placeholder={t.notePlaceholder}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#718096', textAlign: 'right', marginBottom: 6 }}>{notesDraft.length}/100</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="app-btn app-btn-green app-btn-standard" onClick={saveNotes} disabled={savingNotes}
                  style={{ padding: '5px 14px', fontSize: '0.85rem' }}>{savingNotes ? '…' : t.save}</button>
                <button className="app-btn app-btn-standard" onClick={() => { setNotesOpen(false); setNotesDraft(entry.notes ?? ''); }}
                  style={{ padding: '5px 14px', fontSize: '0.85rem', background: '#edf2f7', color: '#2d3748' }}>{t.cancel}</button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {entry.status === 'PENDING' && (
              <button className="app-btn app-btn-standard" onClick={() => doAction('start')} disabled={busy}
                style={{ padding: '6px 16px', fontSize: '0.875rem', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', fontWeight: 600 }}>
                {busy ? '…' : t.startCleaning}
              </button>
            )}
            {entry.status === 'IN_PROGRESS' && (
              <button className="app-btn app-btn-standard" onClick={() => doAction('finish')} disabled={busy}
                style={{ padding: '6px 16px', fontSize: '0.875rem', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', fontWeight: 600 }}>
                {busy ? '…' : t.markDone}
              </button>
            )}
            {!notesOpen && (
              <button className="app-btn app-btn-standard"
                onClick={() => { setNotesOpen(true); setTimeout(() => notesRef.current?.focus(), 50); }}
                style={{ padding: '6px 14px', fontSize: '0.875rem', background: '#f7fafc', color: '#4a5568', border: '1px solid #e2e8f0' }}>
                {entry.notes ? t.editNote : t.addNote}
              </button>
            )}
          </div>

          {/* Timestamps */}
          {(entry.startedAt || entry.completedAt) && (
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#a0aec0', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {entry.startedAt && <span>{t.startedAt} {new Date(entry.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
              {entry.completedAt && <span>{t.completedAt} {new Date(entry.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const CleaningCalendarPage: React.FC = () => {
  const { privateKey } = useParams<{ privateKey: string }>();
  const [schedule, setSchedule] = useState<CleaningSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem(CLEANER_LANG_KEY);
    if (stored === 'cs' || stored === 'en' || stored === 'vi' || stored === 'uk') return stored as Lang;
    const bl = window.navigator.language?.toLowerCase() ?? '';
    if (bl.startsWith('cs')) return 'cs';
    if (bl.startsWith('vi')) return 'vi';
    if (bl.startsWith('uk')) return 'uk';
    return 'en';
  });
  const t = cleaningCalendarText[lang];

  const changeLang = (l: Lang) => { setLang(l); localStorage.setItem(CLEANER_LANG_KEY, l); };

  const fetchSchedule = useCallback(async (showLoading = false) => {
    if (!privateKey) return;
    if (showLoading) setLoading(true);
    try {
      const data = await getCleaningSchedule(privateKey);
      setSchedule(data);
      setError(null);
    } catch {
      if (showLoading) setError('error');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [privateKey]);

  useEffect(() => { fetchSchedule(true); }, [fetchSchedule]);

  useEffect(() => {
    const interval = setInterval(() => fetchSchedule(false), 60_000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchSchedule(false); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [fetchSchedule]);

  const handleUpdate = (updated: CleaningScheduleEntry) => {
    setSchedule(prev => {
      if (!prev) return prev;
      return { ...prev, entries: prev.entries.map(e => e.reservationId === updated.reservationId ? updated : e) };
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ color: '#718096', fontSize: '1.1rem' }}>{t.loading}</div>
      </div>
    );
  }

  if (error || !schedule) {
    return (
      <div style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: 12 }}>⚠</div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 6, color: '#991b1b' }}>{t.notFoundTitle}</div>
        <div style={{ color: '#718096', fontSize: '0.9rem' }}>{t.notFoundDesc}</div>
      </div>
    );
  }

  const rawGroups = groupByDate(schedule.entries);
  // Always show today even when there are no tasks on it
  const groups: [string, CleaningScheduleEntry[]][] = rawGroups.some(([d]) => d === TODAY)
    ? rawGroups
    : [...rawGroups, [TODAY, []] as [string, CleaningScheduleEntry[]]].sort(([a], [b]) => a.localeCompare(b));

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '20px 16px 60px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
            {t.scheduleLabel}
          </div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1a202c' }}>{schedule.cleanerName}</h1>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, paddingTop: 2 }}>
          {([['cs', '🇨🇿'], ['en', '🇬🇧'], ['vi', '🇻🇳'], ['uk', '🇺🇦']] as [Lang, string][]).map(([l, flag]) => (
            <button key={l} type="button" onClick={() => changeLang(l)} style={{
              padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
              border: lang === l ? '2px solid #2563eb' : '2px solid #e2e8f0',
              background: lang === l ? '#eff6ff' : '#fff',
              fontSize: '1.1rem', lineHeight: 1, opacity: lang === l ? 1 : 0.5,
            }}>
              {flag}
            </button>
          ))}
        </div>
      </div>

        {groups.map(([date, entries]) => {
          const today = isToday(date);
          return (
            <div key={date} style={{ marginBottom: 32 }}>
              <div style={today ? {
                background: 'linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)',
                border: '2px solid #fcd34d',
                borderRadius: 14,
                padding: '14px 14px 10px',
              } : {}}>

                {/* Date header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  paddingBottom: 10,
                  borderBottom: `2px solid ${today ? '#fcd34d' : '#e2e8f0'}`,
                }}>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: today ? '#92400e' : '#2d3748' }}>
                    {formatDate(date, lang)}
                  </div>
                  {today && (
                    <span style={{ background: '#f59e0b', color: '#fff', padding: '2px 10px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                      {t.todayBadge}
                    </span>
                  )}
                  {entries.length > 0 && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#a0aec0' }}>
                      {t.taskCount(entries.length)}
                    </span>
                  )}
                </div>

                {today && entries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '18px 0 8px', color: '#92400e' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 6 }}>🎉</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 3 }}>{t.todayAllClear}</div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>{t.todayAllClearSub}</div>
                  </div>
                ) : (
                  entries.map(entry => (
                    <TaskCard
                      key={entry.reservationId}
                      entry={entry}
                      privateKey={privateKey!}
                      defaultExpanded={today}
                      t={t}
                      onUpdate={handleUpdate}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default CleaningCalendarPage;
